import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import Highcharts from 'highcharts';

import abbreviateNumber from 'utils/abbreviateNumber';
import AccountContext from 'contexts/AccountContext';
import { useChartPalette } from 'contexts/ChartPaletteContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import PopupContext from 'contexts/PopupContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import useDateScope from 'hooks/useDateScope';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useValueFormatters from 'hooks/useValueFormatters';
import toAutoInterval from '../toAutoInterval';
import toBarSeries from '../toBarSeries';
import horizontalBar from '../highchartOptions/horizontalBarOptions';
import barOptions from '../highchartOptions/barOptions';

const useBarData = ({
  chartDefinition,
  chartRef,
  isHorizontal,
  isCombo,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
  isHorizontal?: boolean;
  isCombo?: boolean;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
} => {
  const { unitsLocale, weekStartsOn } = useContext(AccountContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const { colors } = useChartPalette();
  const { openMenu } = useContext(DashboardContextMenuContext);
  const { isOpen: isPopupReportOpen } = useContext(PopupContext);
  const { isOpen: isPopupGridOpen } = useContext(DashboardContextMenuContext);

  const [autoInterval, setAutoInterval] = useState<AutoInterval | undefined>();
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);
  const [groupField] = useState(getGroupByField(chartDefinition));

  const { formatMetric } = useValueFormatters();
  const dateScope = useDateScope({});
  const { data } = useV5ChartData(chartDefinition, autoInterval);
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'bar',
    chartDefinition,
    autoInterval,
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    if (chartDefinition.trendByCalendarInterval !== 'auto') {
      return;
    }
    try {
      const aI = Object.values(data)[0].response[0].interval as string;
      const newAI = toAutoInterval(aI);
      setAutoInterval((currentAi) =>
        _.isEqual(currentAi, newAI) ? currentAi : newAI,
      );
    } catch (ex) {
      return undefined;
    }
  }, [chartDefinition.trendByCalendarInterval, data]);

  useEffect(() => {
    if (!isPopupReportOpen) {
      if (chartRef.current) {
        chartRef.current.chart.getSelectedPoints().forEach((p: any) => {
          p.select();
        });
      }
    }
  }, [chartRef, isPopupReportOpen]);

  useEffect(() => {
    if (!isPopupGridOpen) {
      if (chartRef.current) {
        chartRef.current.chart.getSelectedPoints().forEach((p: any) => {
          p.select();
        });
      }
    }
  }, [chartRef, isPopupGridOpen]);

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, data]);

  useEffect(() => {
    if (
      !data ||
      (chartDefinition.trendByCalendarInterval === 'auto' && !autoInterval)
    ) {
      setIsNoDataToDisplay(false);
      return;
    }

    const { series, xAxis, yAxis, tooltip } = toBarSeries(
      data,
      chartDefinition,
      !!isCombo,
      unitsLocale,
      dateScope,
      11,
      weekStartsOnOverride ? weekStartsOnOverride : weekStartsOn,
      formatMetric,
      autoInterval,
      colors,
    );

    const isHistogram =
      !!chartDefinition.dimensionA &&
      chartDefinition.dimensionA.fieldType === 'date';
    const optionBuilder = isHorizontal ? horizontalBar : barOptions;
    // Auto (no explicit labelStep) ties value labels to the axis's own label
    // step (set in toBarSeries) so a label only renders where a date-axis label
    // renders. An explicit labelStep is honoured literally and overrides the
    // axis tie.
    const axisStep: number | undefined = (xAxis as any)?.labels?.step;
    const step = (() => {
      if (chartDefinition.labelStep !== undefined) {
        return chartDefinition.labelStep; // explicit choice wins
      }
      if (axisStep) {
        return axisStep; // Auto = match axis labels
      }
      if (isHistogram) {
        return Math.ceil(series[0] ? series[0].data.length / 10 : 2);
      }
      return undefined;
    })();
    const baseBarOptions = optionBuilder({
      title: ' ',
      showLegend: series.length > 1,
      openMenu,
      groupField,
      fieldType: chartDefinition.dimensionA
        ? chartDefinition.dimensionA.fieldType
        : undefined,
      chartDef: chartDefinition,
      showDataLabels: chartDefinition.showDataLabels,
      fontSize: 11,
      formatMetric,
      dateScope,
      autoInterval,
    });

    const newOptions = {
      ...baseBarOptions,
      series: series.map((s) => ({
        ...s,
        onDrillDown,
        animation: false,
        // `step` is a native Highcharts line option (stepped-line interpolation),
        // so never set it on a line series — only bar/column series use it as the
        // custom value-label thinning key read by the dataLabels formatter.
        step: s.type === 'line' ? undefined : step,
      })),
      tooltip: tooltip ? tooltip : baseBarOptions.tooltip,
      xAxis: {
        ...xAxis,
        padding: 11,
      },
      yAxis: (() => {
        if (yAxis) {
          return yAxis.map((axis) => ({
            ...axis,
            startOnTick: false,
            title: { enabled: false },
            padding: 10,
            labels: {
              ...axis.labels,
              style: { fontSize: 11 },
              formatter: function (
                this: Highcharts.AxisLabelsFormatterContextObject,
              ) {
                const abbreviated = abbreviateNumber(this.value as number);
                if (abbreviated) {
                  return abbreviated.abbreviated.toLowerCase();
                }
                return this.axis.defaultLabelFormatter.call(this);
              },
            },
          }));
        }

        return {
          title: { enabled: false },
          padding: 10,
          labels: {
            style: { fontSize: 11 },
            formatter: function (
              this: Highcharts.AxisLabelsFormatterContextObject,
            ) {
              const abbreviated = abbreviateNumber(this.value as number);
              if (abbreviated) {
                return abbreviated.abbreviated.toLowerCase();
              }
              return this.axis.defaultLabelFormatter.call(this);
            },
          },
        };
      })(),
    };

    if (newOptions.tooltip === undefined) {
      delete newOptions['tooltip'];
    }

    setOptions(newOptions as any as Highcharts.Options);
    setIsNoDataToDisplay(
      (series.length === 1 && series[0].data.length === 0) ||
        (series.length > 1 && !series.some((s) => s.data.length > 0)) ||
        (!!yAxis && yAxis.length === 0),
    );
  }, [
    autoInterval,
    chartDefinition,
    data,
    dateScope,
    formatMetric,
    groupField,
    isCombo,
    isHorizontal,
    onDrillDown,
    openMenu,
    unitsLocale,
    colors,
    weekStartsOn,
    weekStartsOnOverride,
  ]);

  return { options, isNoData: isNoDataToDisplay };
};

export default useBarData;
